import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import {
  ApiService,
  DialogService,
  EntityCacheProvider,
  Device,
  TransformationService,
  LmapService,
  TestAssembleService,
  TestServerConfigService,
  SchedulerFormat,
  TestMode
} from '@nrwl/common-lib';
import { BaseSetupService, BaseSetupTab, setupConfig } from '@nrwl/test-setup';
import { Observable, Subject } from 'rxjs';
import { CODEC_SELECTION, UDPConfigFieldMapping as ConfigFieldMapping } from './models/models';

@Injectable({
  providedIn: 'root'
})
export class UdpSetupService extends BaseSetupService implements OnDestroy {
  state: ActivatedRoute;
  entityCache: EntityCacheProvider;
  transformService: TransformationService;
  lmapService: LmapService;
  testAssembleService: TestAssembleService;
  serverConfigService: TestServerConfigService;
  tabDataChangeEvents = new Subject<any>();
  agentChangeEvents = new Subject<any>();
  testTaskName = 'udp';
  deviceModel = 'vTA';
  selectedOriginator: Device;
  selectedDestination: Device;
  tenantId = 'pass.local';
  controllerConfig = null;
  private getControllerConfig = '/uicomm/getControllerConfiguration';
  private licensePath = '/licensing/licenses';
  private serverConfigPath = '/uicomm/getServerConfig';
  clientToAgentInfo = {
    licenseInfo: null,
    serverConfigInfo: null,
    dmzServerUrl: null,
    activeLicense: 'tf',
    secondaryTestURL: null
  };
  codecSelection = CODEC_SELECTION;
  private readonly unsubscribe: Subject<void> = new Subject();
  constructor(
    apiService: ApiService,
    state: ActivatedRoute,
    dialogService: DialogService,
    dialog: MatDialog,
    entityCache: EntityCacheProvider,
    transformService: TransformationService,
    lmapService: LmapService,
    testAssmebleService: TestAssembleService,
    serverConfigService: TestServerConfigService
  ) {
    super(apiService, dialogService, dialog);
    this.state = state;
    this.tabClass = BaseSetupTab;
    this.entityCache = entityCache;
    this.transformService = transformService;
    this.lmapService = lmapService;
    this.testAssembleService = testAssmebleService;
    this.serverConfigService = serverConfigService;
  }

  openTestSetup(
    setupId: string,
    setupConfig: setupConfig,
    defaultParamsById = {},
    onSuccess = () => {},
    onFail = () => {},
    //eslint-disable-next-line
    agentBasedConfig: any
  ): void {
    super.openTestSetup(
      setupId,
      setupConfig,
      defaultParamsById,
      onSuccess,
      onFail,
      agentBasedConfig
    );
  }

  getAllTestParams(): any {
    this.output['engine-a-info'] = this.getEngineAInfo();
    if (this.output.selectedDestination) {
      this.output['engine-b-info'] = this.getEngineBInfo();
    }
    this.output['udp-parameters'] = this.getUdpTestConfig();
    this.output['streams'] = this.getStreamsGroup();
    return this._getFormatOutput();
  }

  getEngineAInfo(): any {
    const engineInfo = this.output[ConfigFieldMapping.ENGINE_A_INFO];
    const engineAInfo = {};
    if (engineInfo) {
      engineAInfo['address'] = engineInfo['address'];
      engineAInfo['engine-type'] = engineInfo['engine-type'];
      engineAInfo['port'] = engineInfo['port'];
      engineAInfo['test-ip'] = engineInfo['test-ip'];
      engineAInfo['test-subnet-mask'] = engineInfo['test-subnet-mask'];
      engineAInfo['test-gateway'] = engineInfo['test-gateway'];
    }
    return engineAInfo;
  }

  getEngineBInfo(): any {
    const engineInfo = this.output[ConfigFieldMapping.ENGINE_B_INFO];
    const engineBInfo = {};
    if (engineInfo) {
      engineBInfo['address'] = engineInfo['address'];
      engineBInfo['engine-type'] = engineInfo['engine-type'];
      engineBInfo['port'] = engineInfo['port'];
      engineBInfo['test-ip'] = engineInfo['test-ip'];
      engineBInfo['test-subnet-mask'] = engineInfo['test-subnet-mask'];
      engineBInfo['test-gateway'] = engineInfo['test-gateway'];
    }
    return engineBInfo;
  }

  getUdpTestConfig(): any {
    const testconfig = {};
    if (this.output[ConfigFieldMapping.TEST_DURATION] === 'custom') {
      testconfig['isCustomDuration'] = true;
      testconfig['duration'] = this.getTimeDurationInSeconds();
    } else {
      testconfig['isCustomDuration'] = false;
      testconfig['duration'] = this.output[ConfigFieldMapping.TEST_DURATION];
    }
    testconfig['enable-rtt'] = true;
    testconfig['unit-convention'] = this.output[ConfigFieldMapping.STREAM_RATE_CONVENTION];
    testconfig['vlan'] = this.output[ConfigFieldMapping.VLAN];
    return testconfig;
  }

  getTimeDurationInSeconds(): number {
    let duration = 0;
    const hms = this.output[ConfigFieldMapping.TEST_CUSTOM_DURATION].split(':');
    if (hms.length === 3) {
      const hours = parseInt(hms[0], 10);
      const minutes = parseInt(hms[1], 10);
      const seconds = parseInt(hms[2], 10);
      duration = Number(hours) * 60 * 60 + Number(minutes) * 60 + seconds;
    }
    return duration;
  }

  getStreamsGroup(): any {
    const streams = [];
    if (this.output[ConfigFieldMapping.DIRECTION] !== 'unidirectional-b2a') {
      this.output[ConfigFieldMapping.STREAMS_A2B].forEach(stream => {
        if (
          stream[ConfigFieldMapping.STREAM_IN_USE] ||
          stream[ConfigFieldMapping.STREAM_IN_USE] === undefined
        ) {
          const streamData = this.getStreamData(stream, 'tableA');
          streams.push(streamData);
        }
      });
    }
    if (this.output[ConfigFieldMapping.DIRECTION] !== 'unidirectional-a2b') {
      this.output[ConfigFieldMapping.STREAMS_B2A].forEach(stream => {
        if (
          stream[ConfigFieldMapping.STREAM_IN_USE] ||
          stream[ConfigFieldMapping.STREAM_IN_USE] === undefined
        ) {
          const streamData = this.getStreamData(stream, 'tableB');
          streams.push(streamData);
        }
      });
    }
    return streams;
  }
  // eslint-disable-next-line
  getStreamData(stream: any, tableChange: string): any {
    let streamData = {};
    const output = {};
    output[ConfigFieldMapping.STREAM_NUMBER] = stream[ConfigFieldMapping.STREAM_NUMBER];
    output[ConfigFieldMapping.STREAM_NAME] = stream[ConfigFieldMapping.STREAM_NAME];
    output[ConfigFieldMapping.FRAME_SIZE] = this.removeCustom(
      stream[ConfigFieldMapping.FRAME_SIZE]
    );
    output['actual-frame-size'] = stream[ConfigFieldMapping.FRAME_SIZE];
    output['packet-loss-ratio-threshold'] = Number(stream[ConfigFieldMapping.POCKET_LOSS_RATIO]);
    output[ConfigFieldMapping.JITTER_THRESHOLD] = Number(
      stream[ConfigFieldMapping.JITTER_THRESHOLD]
    );
    output[ConfigFieldMapping.STREAM_TYPE] = stream[ConfigFieldMapping.STREAM_TYPE];
    if (stream[ConfigFieldMapping.STREAM_TYPE] === 'Video') {
      output['video-type'] = stream[ConfigFieldMapping.VIDEO_TYPE];
      output[ConfigFieldMapping.COMPRESSION] = stream[ConfigFieldMapping.COMPRESSION];
      output[ConfigFieldMapping.CHANNELS] = stream[ConfigFieldMapping.CHANNELS];
    } else if (stream[ConfigFieldMapping.STREAM_TYPE] === 'Voice') {
      output[ConfigFieldMapping.CODEC] = stream[ConfigFieldMapping.CODEC];
      output['sampling-rate'] = stream[ConfigFieldMapping.SAMPLING_RATE];
      output[ConfigFieldMapping.CALLS] = stream[ConfigFieldMapping.CALLS];
    }
    output[ConfigFieldMapping.STREAM_NAME] = stream[ConfigFieldMapping.STREAM_NAME];
    output[ConfigFieldMapping.UDP_PORT] = stream[ConfigFieldMapping.UDP_PORT].toString();
    if (this.output[ConfigFieldMapping.DIRECTION] === 'unidirectional-a2b') {
      output['mode'] = 'engine-a-to-engine-b';
      output['bw'] = stream[ConfigFieldMapping.UDP_BW_A2B].toString();
      output['ir'] = stream[ConfigFieldMapping.STREAM_L2_IR_A2B].toString();
    }
    if (this.output[ConfigFieldMapping.DIRECTION] === 'unidirectional-b2a') {
      output['mode'] = 'engine-b-to-engine-a';
      output['bw'] = stream[ConfigFieldMapping.UDP_BW_B2A].toString();
      output['ir'] = stream[ConfigFieldMapping.STREAM_L2_IR_B2A].toString();
    }
    if (this.output[ConfigFieldMapping.DIRECTION] === 'sequential-bidirectional') {
      output['mode'] = 'bidirsequential';
      output['bw'] =
        tableChange === 'tableA'
          ? stream[ConfigFieldMapping.UDP_BW_A2B].toString()
          : stream[ConfigFieldMapping.UDP_BW_B2A].toString();
      output['ir'] =
        tableChange === 'tableA'
          ? stream[ConfigFieldMapping.STREAM_L2_IR_A2B].toString()
          : stream[ConfigFieldMapping.STREAM_L2_IR_B2A].toString();
    }
    if (this.output[ConfigFieldMapping.DIRECTION] === 'concurrent-bidirectional') {
      output['mode'] = 'bidirectional';
      output['bw'] =
        tableChange === 'tableA'
          ? stream[ConfigFieldMapping.UDP_BW_A2B].toString()
          : stream[ConfigFieldMapping.UDP_BW_B2A].toString();
      output['ir'] =
        tableChange === 'tableA'
          ? stream[ConfigFieldMapping.STREAM_L2_IR_A2B].toString()
          : stream[ConfigFieldMapping.STREAM_L2_IR_B2A].toString();
    }
    streamData = this.removeNullValues(output);
    return streamData;
  }
  // eslint-disable-next-line
  removeCustom(str): any {
    if (str?.indexOf('custom:') >= 0) {
      // eslint-disable-next-line
      let parts = str.split('custom:');
      return parts.join('');
    }
    return str;
  }
  // eslint-disable-next-line
  createLMAPObject(dataModel: any): any {
    const testPointA = this.output[ConfigFieldMapping.SELECTED_ORIGINATOR]?.deviceData;
    const testPointB = this.output[ConfigFieldMapping.SELECTED_DESTINATION]?.deviceData;
    testPointA['id'] = testPointA.uniqueId;
    if (testPointB) {
      testPointB['id'] = testPointB.uniqueId;
    }
    const serverConfig = this.serverConfigService.getCollectorConfig();
    const collectorURL = this.lmapService.buildCollectorPostUrl(serverConfig, this.deviceModel);
    const testSchedule = this.createTestSchedule(testPointA, collectorURL, dataModel);
    const controllerConfig = this.createControllerConfig(testPointA, testPointB, testSchedule);
    if (!this.output.scheduler?.repeatInterval) {
      this.output[ConfigFieldMapping.RUN_TYPE] = 'on-demand';
    }
    controllerConfig.test['test-list-data']['runType'] = this.output.runType;
    return controllerConfig;
  }
  // eslint-disable-next-line
  createTestSchedule(testPointA: any, collectorURL: string, dataModel: any): any {
    const tagOptions: Array<string> = ['monitor-type:on-going'];
    const schedObj: SchedulerFormat = { ...this.output[ConfigFieldMapping.SCHEDULER] };
    // create the test schedule
    const testScheduleName = this.output[ConfigFieldMapping.ALIAS];
    const testActionName = testScheduleName + '-action';
    let testEventName = testScheduleName + '-schedule-event';

    // For start immediate and repeat once test
    if (!schedObj.timeStamp && !schedObj.repeatInterval) {
      testEventName = 'immediate';
    }

    const allTestParams = {};
    allTestParams['udp:udp'] = dataModel;

    const actionOption = {
      id: this.testTaskName,
      value: JSON.stringify(allTestParams)
    };
    const actionOption2 = {
      id: 'role',
      value: 'primary'
    };

    const optionArray = [];
    optionArray.push(actionOption);
    optionArray.push(actionOption2);

    if (testPointA.phyId !== '') {
      const actionOptionPhy = {
        id: 'phy-id',
        value: testPointA.phyId
      };
      optionArray.push(actionOptionPhy);
    }
    const tags = this.output.scheduler?.repeatInterval ? tagOptions : null;
    const action = this.lmapService.createAction(
      testActionName,
      this.testTaskName,
      optionArray,
      null,
      tags
    );
    const actionArray = [];
    actionArray.push(action);
    // while report schedule references could be created here they will be added
    //automatically to this schedule via addResultScheduleAndEvent()
    const testSchedule = this.lmapService.createSchedule(
      testScheduleName,
      testEventName,
      actionArray
    );

    // build the LMAP configuration (test schedule + result schedules + all events other than those predefined by MA such as immediate)
    let lmapified = this.lmapService.addSchedule(testSchedule);
    this.lmapService.CONST.resultFormatType = 'v2';
    // final results are mandatory
    lmapified = this.lmapService.addResultScheduleAndEvent(
      lmapified,
      testScheduleName,
      testEventName,
      'immediate',
      'final',
      collectorURL
    );
    // rt results are technically optional, but mandatory for us
    // set to 1 second interval
    lmapified = this.lmapService.addResultScheduleAndEvent(
      lmapified,
      testScheduleName,
      testEventName,
      'periodic',
      'rt',
      collectorURL,
      schedObj.repeatInterval,
      schedObj.timeStamp,
      null
    );
    return lmapified;
  }
  // eslint-disable-next-line
  createControllerConfig(testPointA, testPointB, lmapified): any {
    let controllerConfig = this.testAssembleService.buildPrimaryTestConfig(
      testPointA,
      this.testTaskName,
      lmapified
    );
    if (this.output[ConfigFieldMapping.TEST_MODE] !== TestMode.CLIENT_TO_MA) {
      const secondaryConfig = JSON.parse(JSON.stringify(lmapified));
      let foundRole = false,
        foundPhyId = false;
      // secondary config is created as a copy of primary config, so need to update for role and phy-id
      for (const x in secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0]
        .option) {
        // update role to secondary
        if (
          !foundRole &&
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[x].id ===
            'role'
        ) {
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[
            x
          ].value = 'secondary';
          foundRole = true;
        }
        // remove phy-id object if secondary device doesn't have multiple ports
        if (!foundPhyId && !testPointB.phyId) {
          if (
            secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[x]
              .id === 'phy-id'
          ) {
            foundPhyId = true;
            secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option.splice(
              x,
              1
            );
          }
        }

        // set the value of phy-id
        if (
          !foundPhyId &&
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[x].id ===
            'phy-id'
        ) {
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[
            x
          ].value = testPointB.phyId ? testPointB.phyId : ''; // set value if exists, blank otherwise
          foundPhyId = true;
        }
        if (foundRole && foundPhyId) {
          break;
        }
      }

      // case when primary device is single port, so need to add phy-id object for secondary config
      if (!foundPhyId) {
        if (testPointB.phyId) {
          const optionObjIndex =
            secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option.length;
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[
            optionObjIndex
          ] = {};
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[
            optionObjIndex
          ].id = 'phy-id';
          secondaryConfig['ietf-lmap-control:lmap'].schedules.schedule[0].action[0].option[
            optionObjIndex
          ].value = testPointB.phyId;
        }
      }
      controllerConfig = this.testAssembleService.addSecondaryTestConfig(
        controllerConfig,
        'secondaryConfig',
        testPointB,
        this.testTaskName,
        secondaryConfig
      );
    } else {
      controllerConfig.test['controller'] = {};
      controllerConfig.test['controller']['max-exec-count'] =
        this.output[ConfigFieldMapping.MAX_EXECUTION_COUNTS];
      controllerConfig.test['controller']['expiration-date'] =
        this.output[ConfigFieldMapping.EXPIRATION_DATE];
    }
    controllerConfig.test['extended-config'] = {};
    controllerConfig.test['test-list-data']['interval'] = this.output.scheduler?.repeatInterval;
    controllerConfig.test['test-list-data']['mode'] = this.output[ConfigFieldMapping.TEST_MODE];
    controllerConfig.test['test-list-data']['customer'] = {
      name: this.output[ConfigFieldMapping.CUSTOMER],
      contact: this.output.contact,
      email: this.output.email,
      phone: this.output.phone
    };
    controllerConfig.test['extended-config']['customerInfo'] = {
      name: this.output[ConfigFieldMapping.CUSTOMER],
      contact: this.output.contact,
      email: this.output.email,
      phone: this.output.phone
    };
    controllerConfig.test['extended-config']['technicianInfo'] = {
      name: this.output[ConfigFieldMapping.TECHINICIAN],
      id: this.output.technicianId
    };
    controllerConfig.test['extended-config']['testInfo'] = {
      name: this.output[ConfigFieldMapping.ALIAS],
      comments : this.output.comments
    };
    controllerConfig.test['extended-config']['test'] = {
      mode: this.output[ConfigFieldMapping.TEST_MODE],
      nameOnProbe: 'udp',
      selected: true,
      testType: 'UDP'
    };
    controllerConfig.test['extended-config']['direction'] = {
      name: this.output[ConfigFieldMapping.DIRECTION]
    };
    controllerConfig.test['extended-config']['unit-convention'] =
      this.output[ConfigFieldMapping.STREAM_RATE_CONVENTION];
    controllerConfig.test['extended-config']['TestDuration'] =
      this.output[ConfigFieldMapping.TEST_DURATION];
    controllerConfig.test['extended-config']['pluginConfig'] = this.getExtendedPlugingConfig();
    controllerConfig.test['extended-config']['testPoints'] = [
      {
        tag: 'Test Point A',
        selected: true,
        type: 'inventory',
        id: testPointA.id,
        testPortPhyId: testPointA.phyId,
        name: testPointA.nameId,
        vendor: 'viavi',
        model: testPointA.model.model,
        version: testPointA.params.find(param => param.name === 'version').value,
        ip: testPointA.ipaddress,
        port: '443'
      }
    ];
    if (this.output[ConfigFieldMapping.TEST_MODE] !== TestMode.CLIENT_TO_MA) {
      controllerConfig.test['extended-config']['testPoints'].push({
        tag: 'Test Point B',
        selected: true,
        type: 'inventory',
        id: testPointB.id,
        testPortPhyId: testPointB.phyId,
        name: testPointB.nameId,
        vendor: 'viavi',
        model: testPointB.model.model,
        version: testPointB.params.find(param => param.name === 'version').value,
        ip: testPointB.ipaddress,
        port: '443'
      });
    }
    controllerConfig.test['ipv6'] = 'no';
    return controllerConfig;
  }

  getExtendedPlugingConfig(): any {
    const pluginConfig = {
      connections: {
        connections: [{ streams: this.getStreamsDetails() }]
      },
      setup: {
        bandwidthConvention:
          this.output[ConfigFieldMapping.STREAM_RATE_CONVENTION] === 'l2-mbps' ? 'ir' : 'ulr',
        expirationDate: this.output[ConfigFieldMapping.EXPIRATION_DATE],
        maximumExecutionCount: this.output[ConfigFieldMapping.MAX_EXECUTION_COUNTS] || 'Unlimited',
        vlanPresent: this.output[ConfigFieldMapping.VLAN]
      }
    };
    return pluginConfig;
  }

  getStreamsDetails(): any {
    let newStreamsA2B = [];
    let newStreamsB2A = [];
    let streamObj = {};
    const allStreamsA2B = this.output[ConfigFieldMapping.STREAMS_A2B];
    if (allStreamsA2B.length > 0) {
      allStreamsA2B.forEach(stream => {
        streamObj['strmNumber'] = stream[ConfigFieldMapping.STREAM_NUMBER];
        streamObj['strmName'] = stream[ConfigFieldMapping.STREAM_NAME];
        streamObj['strmUdpType'] = stream[ConfigFieldMapping.STREAM_TYPE];
        streamObj['strmVideoType'] = stream[ConfigFieldMapping.VIDEO_TYPE] || 'HDTV';
        streamObj['strmVideoCompression'] = stream[ConfigFieldMapping.COMPRESSION] || 'MPEG4';
        streamObj['strmNumberChannels'] = stream[ConfigFieldMapping.CHANNELS] || '1';
        streamObj['strmVoiceCodec'] = stream[ConfigFieldMapping.CODEC] || 'G711';
        streamObj['strmVoiceSampleRate'] = stream[ConfigFieldMapping.SAMPLING_RATE] || 10;
        streamObj['strmNumberCalls'] = stream[ConfigFieldMapping.CALLS] || '10';
        streamObj['strmUdpDirection'] = this.output[ConfigFieldMapping.DIRECTION];
        streamObj['strmSlaULROrIR'] = stream[ConfigFieldMapping.STREAM_L2_IR_A2B] || '10';
        streamObj['strmSlaCir'] = stream[ConfigFieldMapping.UDP_BW_A2B] || '9.10';
        streamObj['strmFrameJitter'] = stream[ConfigFieldMapping.JITTER_THRESHOLD];
        streamObj['strmFrameSize'] = stream[ConfigFieldMapping.FRAME_SIZE];
        streamObj['strmPacketLossRatio'] = stream[ConfigFieldMapping.POCKET_LOSS_RATIO];
        streamObj['strmUdpPort'] = stream[ConfigFieldMapping.UDP_PORT];
        if (stream[ConfigFieldMapping.STREAM_IN_USE] === undefined) {
          streamObj['strmInUse'] = true;
        } else {
          streamObj['strmInUse'] = stream[ConfigFieldMapping.STREAM_IN_USE];
        }
        streamObj = this.removeNullValues(streamObj);
        newStreamsA2B = [...newStreamsA2B, { ...streamObj }];
      });
    }
    let streamObjB2A = {};
    const allStreamsB2A = this.output[ConfigFieldMapping.STREAMS_B2A];
    if (allStreamsB2A.length > 0) {
      allStreamsB2A.forEach(stream => {
        streamObjB2A['strmNumber'] = stream[ConfigFieldMapping.STREAM_NUMBER];
        streamObjB2A['strmName'] = stream[ConfigFieldMapping.STREAM_NAME];
        streamObjB2A['strmUdpType'] = stream[ConfigFieldMapping.STREAM_TYPE];
        streamObjB2A['strmVideoType'] = stream[ConfigFieldMapping.VIDEO_TYPE] || 'HDTV';
        streamObjB2A['strmVideoCompression'] = stream[ConfigFieldMapping.COMPRESSION] || 'MPEG4';
        streamObjB2A['strmNumberChannels'] = stream[ConfigFieldMapping.CHANNELS] || '1';
        streamObjB2A['strmVoiceCodec'] = stream[ConfigFieldMapping.CODEC] || 'G711';
        streamObjB2A['strmVoiceSampleRate'] = stream[ConfigFieldMapping.SAMPLING_RATE] || 10;
        streamObjB2A['strmNumberCalls'] = stream[ConfigFieldMapping.CALLS] || '10';
        streamObjB2A['strmUdpDirection'] = this.output[ConfigFieldMapping.DIRECTION];
        streamObjB2A['strmSlaULROrIR2'] = stream[ConfigFieldMapping.STREAM_L2_IR_B2A] || '10';
        streamObjB2A['strmSlaCir2'] = stream[ConfigFieldMapping.UDP_BW_B2A] || '9.10';
        streamObjB2A['strmFrameJitter'] = stream[ConfigFieldMapping.JITTER_THRESHOLD];
        streamObjB2A['strmFrameSize'] = stream[ConfigFieldMapping.FRAME_SIZE];
        streamObjB2A['strmPacketLossRatio'] = stream[ConfigFieldMapping.POCKET_LOSS_RATIO];
        streamObjB2A['strmUdpPort'] = stream[ConfigFieldMapping.UDP_PORT];
        if (stream[ConfigFieldMapping.STREAM_IN_USE] === undefined) {
          streamObjB2A['strmInUse'] = true;
        } else {
          streamObjB2A['strmInUse'] = stream[ConfigFieldMapping.STREAM_IN_USE];
        }
        streamObjB2A = this.removeNullValues(streamObjB2A);
        newStreamsB2A = [...newStreamsB2A, { ...streamObjB2A }];
      });
    }
    if (this.output[ConfigFieldMapping.DIRECTION] === 'unidirectional-a2b') {
      return newStreamsA2B;
    } else if (this.output[ConfigFieldMapping.DIRECTION] === 'unidirectional-b2a') {
      return newStreamsB2A;
    } else {
      const bothstreams = [...newStreamsA2B, ...newStreamsB2A];
      return bothstreams;
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.unsubscribe();
  }

  getControllerConfiguration(): void {
    const url = `${this.getControllerConfig}`;
    this.controllerConfig = this.apiService.post$<any>(url);
  }

  getScheduleTimeStamp(timestamp: string): string {
    if (timestamp.toLowerCase() === 'now') {
      return null; //To start the test immediately -Need to update based on backend value
    } else {
      const currentTimestamp = new Date().getTime();
      if (Number(timestamp) < currentTimestamp) {
        return null;
      } else {
        return new Date(Number(timestamp)).toISOString();
      }
    }
  }

  getLicenseData(): Observable<any> {
    return this.apiService.get$(this.licensePath);
  }

  getServerConfig(): Observable<any> {
    return this.apiService.post$(this.serverConfigPath);
  }
  // eslint-disable-next-line
  generateSecondaryTestURL(serverConfig: any, shaID: string): any {
    let port = null;
    let proxyIP = null;
    let url = null;
    if (
      this.output.selectedOriginator &&
      this.output.selectedOriginator.deviceData &&
      this.output.selectedOriginator.deviceData.capabilities
    ) {
      if (
        this.output.selectedOriginator.deviceData.capabilities.proxy &&
        this.output.selectedOriginator.deviceData.capabilities.proxy['local-port']
      ) {
        // if there is no proxy will not have the secondary URL
        port = this.output.selectedOriginator.deviceData.capabilities['proxy']['local-port'];
      }
      if (
        this.output.selectedOriginator.deviceData.capabilities.udp &&
        this.output.selectedOriginator.deviceData.capabilities['udp']['public-access-ip']
      ) {
        proxyIP = this.output.selectedOriginator.deviceData.capabilities['udp']['public-access-ip'];
      }
      if (port && proxyIP) {
        if (
          serverConfig &&
          serverConfig['vibe-public-access'].protocol &&
          serverConfig['vibe-public-access'].path
        ) {
          url = serverConfig['vibe-public-access'].protocol + '://';
          url += proxyIP + ':' + port;
          url += serverConfig['vibe-public-access'].path + shaID;
          this.clientToAgentInfo.secondaryTestURL = url;
        }
      }
    }
    return url;
  }
  // eslint-disable-next-line
  updateStreamTable(convention, vlan, changeType, streamsA2B, streamsB2A): void {
    if (!this.output[ConfigFieldMapping.STREAMS_A2B]) {
      this.output[ConfigFieldMapping.STREAMS_A2B] = [...streamsA2B];
    }
    if (!this.output[ConfigFieldMapping.STREAMS_B2A]) {
      this.output[ConfigFieldMapping.STREAMS_B2A] = [...streamsB2A];
    }
    if (this.output[ConfigFieldMapping.STREAMS_A2B]?.length > 0) {
      this.output[ConfigFieldMapping.STREAMS_A2B].forEach(stream => {
        const frameSize = parseInt(this.removeCustom(stream['frame-size']));
        if (stream['stream-type'] === 'Data') {
          if (stream['l2-ir-a2b']) {
            stream['udp-bw-a2b'] = this.updateBandwidthValue(
              stream['l2-ir-a2b'],
              vlan,
              frameSize,
              convention
            );
          }
        } else if (stream['stream-type'] === 'Voice') {
          if (stream['udp-bw-a2b']) {
            stream['l2-ir-a2b'] = this.updateIrValue(
              stream['udp-bw-a2b'],
              vlan,
              frameSize,
              convention
            );
          }
        } else {
          if (changeType === 'convention') {
            if (stream['udp-bw-a2b']) {
              stream['l2-ir-a2b'] = this.updateIrValue(
                stream['udp-bw-a2b'],
                vlan,
                frameSize,
                convention
              );
            }
          } else if (changeType === 'vlan') {
            if (stream['l2-ir-a2b']) {
              stream['udp-bw-a2b'] = this.updateBandwidthValue(
                stream['l2-ir-a2b'],
                vlan,
                frameSize,
                convention
              );
            }
          }
        }
      });
    }
    if (this.output[ConfigFieldMapping.STREAMS_B2A]?.length > 0) {
      this.output[ConfigFieldMapping.STREAMS_B2A].forEach(stream => {
        const frameSize = parseInt(this.removeCustom(stream['frame-size']));
        if (stream['stream-type'] === 'Data') {
          if (stream['l2-ir-b2a']) {
            stream['udp-bw-b2a'] = this.updateBandwidthValue(
              stream['l2-ir-b2a'],
              vlan,
              frameSize,
              convention
            );
          }
        } else if (stream['stream-type'] === 'Voice') {
          if (stream['udp-bw-b2a']) {
            stream['l2-ir-b2a'] = this.updateIrValue(
              stream['udp-bw-b2a'],
              vlan,
              frameSize,
              convention
            );
          }
        } else {
          if (changeType === 'convention') {
            if (stream['udp-bw-b2a']) {
              stream['l2-ir-b2a'] = this.updateIrValue(
                stream['udp-bw-b2a'],
                vlan,
                frameSize,
                convention
              );
            }
          } else if (changeType === 'vlan') {
            if (stream['l2-ir-b2a']) {
              stream['udp-bw-b2a'] = this.updateBandwidthValue(
                stream['l2-ir-b2a'],
                vlan,
                frameSize,
                convention
              );
            }
          }
        }
      });
    }
  }
  // eslint-disable-next-line
  updateBandwidthValue(value, ethFrameOverhead, frameSize, convention): any {
    let result;
    if (convention === 'l1-mbps') {
      result = (value / (frameSize + 20)) * (frameSize - ethFrameOverhead);
    } else {
      result = (value / frameSize) * (frameSize - ethFrameOverhead);
    }
    return (result = this.roundToPrecision(result, 2));
  }
  // eslint-disable-next-line
  roundToPrecision(number, precision): any {
    if (!number || !precision) {
      return number;
    }
    return (+(Math.round(+(number + 'e' + precision)) + 'e' + -precision)).toFixed(precision);
  }
  // eslint-disable-next-line
  updateIrValue(value, ethFrameOverhead, frameSize, convention): any {
    let result;
    if (convention === 'l1-mbps') {
      result = (value / (frameSize - ethFrameOverhead)) * (frameSize + 20);
    } else {
      result = (value * frameSize) / (frameSize - ethFrameOverhead);
    }
    result = this.roundToPrecision(result, 2);
    return result.toString();
  }

  //------------------------------------------------ Wizard control ----------------------------------------------------

  toggleTabChange(index: number): void {
    super.toggleTabChange(index);
    this.tabDataChangeEvents.next(index);
  }

  setOriginatorAgent(device: Device): void {
    this.selectedOriginator = device;
    this.agentChangeEvents.next(device);
  }
  getOriginatorAgent(): any {
    return this.selectedOriginator;
  }
}
